import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDollarSign,
  faClock,
  faRocket,
  faChartLine,
  faSpinner,
  faExclamationTriangle,
  faSync,
  faFileInvoice,
  faPercentage,
  faHourglassHalf,
  faSackDollar,
  faArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import MainLayout from '../Layout/MainLayout';

/**
 * Generic Impact Dashboard Component
 * Works for any agent. Pass agentId via prop OR URL param.
 * Mirrors the SpendAnalysis pattern.
 */
const ImpactDashboard = ({ agentId: propAgentId, agentName: propAgentName }) => {
  const { agentId: routeAgentId } = useParams();
  const agentId = propAgentId || (routeAgentId ? parseInt(routeAgentId, 10) : 3);
  const { token } = useAuth();
  const navigate = useNavigate();

  // Map agent_id to the correct agent landing page
  const getBackRoute = (id) => {
    switch (parseInt(id, 10)) {
      case 3:  return '/agents/3';   // Bill Processing
      case 12: return '/agents/12';  // Sales Order NS
      case 13: return '/agents/13';  // Bank Statement
      case 15: return '/agents/15';  // Sales Order SF
      default: return `/agents/${id}`;
    }
  };
  const handleBack = () => navigate(getBackRoute(agentId));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodDays, setPeriodDays] = useState(90);

  useEffect(() => {
    if (agentId) {
      fetchImpact();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, periodDays]);

  const fetchImpact = async () => {
    try {
      setLoading(true);
      setError(null);
      const url = `/api/impact-dashboard?agent_id=${agentId}&period_days=${periodDays}`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        throw new Error('Failed to fetch impact metrics');
      }
      const json = await res.json();
      if (!json.success) {
        throw new Error(json.error || 'Failed to load impact');
      }
      setData(json);
    } catch (err) {
      console.error('Impact dashboard error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fmtMoney = (n) =>
    typeof n === 'number'
      ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
      : '$0';
  const fmtNum = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : '0');

  const accentBar = {
    blue: 'before:bg-blue-500',
    purple: 'before:bg-purple-500',
    green: 'before:bg-emerald-500',
    orange: 'before:bg-amber-500',
    pink: 'before:bg-pink-500',
    teal: 'before:bg-teal-500',
  };

  const renderHero = (summary, config) => {
    const moneySaved = summary?.total_money_saved ?? 0;
    const timeHours = summary?.total_time_saved_hours ?? 0;
    const agentName = propAgentName || config?.agent_name || 'Agent';
    return (
      <motion.div
        className="relative overflow-hidden bg-gradient-to-br from-indigo-500 via-purple-500 to-purple-700 text-white rounded-2xl p-8 shadow-xl"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Decorative blobs */}
        <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-56 h-56 bg-white/5 rounded-full blur-2xl pointer-events-none" />
        <div className="relative">
          <div className="text-xs uppercase tracking-widest opacity-85 font-semibold">
            {agentName} delivered
          </div>
          <div className="text-6xl font-extrabold leading-tight my-2 drop-shadow">
            {fmtMoney(moneySaved)}
          </div>
          <div className="text-base opacity-90 mb-4">
            in business value · last {periodDays} days
          </div>
          <div className="text-sm opacity-95 inline-flex items-center gap-2 bg-white/15 backdrop-blur px-4 py-2 rounded-full">
            <FontAwesomeIcon icon={faClock} />
            Equivalent to <strong className="font-bold">{fmtNum(Math.round(timeHours))} hours</strong> of manual work
          </div>
        </div>
      </motion.div>
    );
  };

  const renderKpiCards = (summary, config) => {
    const cards = [
      {
        icon: faFileInvoice,
        label: `${config.unit_plural || 'documents'} processed`,
        value: fmtNum(summary.total_count),
        accent: 'blue',
      },
      {
        icon: faHourglassHalf,
        label: 'Time saved',
        value: `${fmtNum(Math.round(summary.total_time_saved_hours))} hrs`,
        accent: 'purple',
      },
      {
        icon: faSackDollar,
        label: 'Money saved',
        value: fmtMoney(summary.total_money_saved),
        accent: 'green',
      },
      {
        icon: faChartLine,
        label: 'ROI',
        value: `${fmtNum(summary.roi_percent)}%`,
        accent: 'orange',
      },
      {
        icon: faRocket,
        label: 'Avg processing',
        value: `${summary.avg_processing_time_min} min`,
        accent: 'pink',
      },
      {
        icon: faPercentage,
        label: 'Touch-free rate',
        value: `${summary.touch_free_rate}%`,
        accent: 'teal',
      },
    ];

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map((c, idx) => (
          <motion.div
            key={c.label}
            className={`relative bg-white dark:bg-gray-800 rounded-xl p-4 border border-slate-200 dark:border-gray-700 overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:w-1 before:h-full ${accentBar[c.accent]} shadow-sm hover:shadow-md transition-shadow`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
          >
            <div className="text-slate-400 dark:text-slate-500 text-base mb-2">
              <FontAwesomeIcon icon={c.icon} />
            </div>
            <div className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-1 truncate" title={c.value}>
              {c.value}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {c.label}
            </div>
          </motion.div>
        ))}
      </div>
    );
  };

  const renderCostComparison = (summary) => {
    const manual = summary.manual_cost_total || 0;
    const ai = summary.ai_cost_total || 0;
    const max = Math.max(manual, ai, 1);
    const manualPct = (manual / max) * 100;
    const aiPct = (ai / max) * 100;
    const net = manual - ai;
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
            Cost: Manual vs AI
          </div>
          <div className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
            Save {fmtMoney(net)}
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-[60px_1fr_90px] sm:grid-cols-[80px_1fr_100px] items-center gap-3">
            <div className="text-sm text-slate-600 dark:text-slate-300 font-medium">Manual</div>
            <div className="bg-slate-100 dark:bg-gray-700 rounded h-5 overflow-hidden">
              <motion.div
                className="h-full rounded bg-gradient-to-r from-red-500 to-red-400"
                initial={{ width: 0 }}
                animate={{ width: `${manualPct}%` }}
                transition={{ duration: 0.6 }}
              />
            </div>
            <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 text-right tabular-nums">
              {fmtMoney(manual)}
            </div>
          </div>
          <div className="grid grid-cols-[60px_1fr_90px] sm:grid-cols-[80px_1fr_100px] items-center gap-3">
            <div className="text-sm text-slate-600 dark:text-slate-300 font-medium">AI</div>
            <div className="bg-slate-100 dark:bg-gray-700 rounded h-5 overflow-hidden">
              <motion.div
                className="h-full rounded bg-gradient-to-r from-emerald-500 to-emerald-400"
                initial={{ width: 0 }}
                animate={{ width: `${aiPct}%` }}
                transition={{ duration: 0.6, delay: 0.1 }}
              />
            </div>
            <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 text-right tabular-nums">
              {fmtMoney(ai)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTrend = (trend) => {
    if (!trend || trend.length === 0) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-5 shadow-sm">
          <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-4">
            Savings Trend
          </div>
          <div className="text-sm text-slate-400 dark:text-slate-500 text-center py-8">
            No data in this window yet.
          </div>
        </div>
      );
    }
    const maxSaved = Math.max(...trend.map((t) => t.money_saved), 1);
    const W = 600;
    const H = 200;
    const padding = 24;
    const innerW = W - padding * 2;
    const innerH = H - padding * 2;
    const step = trend.length > 1 ? innerW / (trend.length - 1) : 0;
    const pointsArr = trend.map((t, i) => {
      const x = padding + i * step;
      const y = padding + innerH - (t.money_saved / maxSaved) * innerH;
      return { x, y, t };
    });
    const points = pointsArr.map((p) => `${p.x},${p.y}`).join(' ');
    // Area fill
    const areaPath = `M ${pointsArr[0].x},${padding + innerH} L ${points
      .split(' ')
      .join(' L ')} L ${pointsArr[pointsArr.length - 1].x},${padding + innerH} Z`;

    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
            Daily savings trend
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Peak <strong className="text-slate-700 dark:text-slate-200">{fmtMoney(maxSaved)}</strong>
          </div>
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full" style={{ height: '200px' }}>
          <defs>
            <linearGradient id="impactArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill="url(#impactArea)" />
          <polyline fill="none" stroke="#10b981" strokeWidth="2.5" points={points} />
          {pointsArr.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r="3.5" fill="#10b981" stroke="white" strokeWidth="1.5">
              <title>{`${p.t.date}: ${fmtMoney(p.t.money_saved)} (${p.t.count})`}</title>
            </circle>
          ))}
        </svg>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
          {trend.length} day{trend.length !== 1 ? 's' : ''} with activity
        </div>
      </div>
    );
  };

  const renderTopEntities = (entities, config) => {
    const label = config.entity_label || 'Entities';
    if (!entities || entities.length === 0) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-5 shadow-sm">
          <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-4">
            Top {label}
          </div>
          <div className="text-sm text-slate-400 dark:text-slate-500 text-center py-8">
            No mapped {label.toLowerCase()} yet.
          </div>
        </div>
      );
    }
    const max = Math.max(...entities.map((e) => e.count), 1);
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-5 shadow-sm">
        <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-4">
          Top {label}
        </div>
        <div className="flex flex-col gap-2.5">
          {entities.map((e, idx) => (
            <div
              className="grid grid-cols-[1fr_140px] sm:grid-cols-[1.5fr_2fr_60px] items-center gap-3"
              key={`${e.name}-${idx}`}
            >
              <div
                className="text-sm text-slate-700 dark:text-slate-200 truncate font-medium"
                title={e.name}
              >
                {e.name}
              </div>
              <div className="hidden sm:block bg-slate-100 dark:bg-gray-700 rounded h-3 overflow-hidden">
                <motion.div
                  className="bg-gradient-to-r from-indigo-500 to-purple-500 h-full rounded"
                  initial={{ width: 0 }}
                  animate={{ width: `${(e.count / max) * 100}%` }}
                  transition={{ duration: 0.5, delay: idx * 0.04 }}
                />
              </div>
              <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 text-right tabular-nums">
                {fmtNum(e.count)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <MainLayout>
      <div className="p-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="px-3 py-2 border border-slate-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
              title="Back to agent"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              <span className="text-sm font-medium">Back</span>
            </button>
            <h1 className="m-0 text-2xl font-bold text-slate-800 dark:text-slate-100">
              <FontAwesomeIcon icon={faDollarSign} className="mr-2 text-emerald-500" />
              Impact Dashboard
            </h1>
          </div>
          <div className="flex gap-2 items-center">
            <select
              className="px-3 py-2 border border-slate-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 dark:text-slate-200 text-sm cursor-pointer"
              value={periodDays}
              onChange={(e) => setPeriodDays(parseInt(e.target.value, 10))}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 365 days</option>
            </select>
            <button
              className="px-3 py-2 border border-slate-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-slate-500 dark:text-slate-400 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-gray-700"
              onClick={fetchImpact}
              disabled={loading}
              title="Refresh"
            >
              <FontAwesomeIcon icon={faSync} spin={loading} />
            </button>
          </div>
        </div>

        {loading && (
          <div className="p-10 text-center text-slate-500 dark:text-slate-400">
            <FontAwesomeIcon icon={faSpinner} spin /> Loading impact metrics...
          </div>
        )}

        {error && !loading && (
          <div className="p-6 rounded-xl border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 text-center">
            <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
          </div>
        )}

        {!loading && !error && data && data.summary?.total_count === 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-xl p-6 text-center">
            <div className="text-amber-700 dark:text-amber-300 font-semibold mb-1">
              No activity in the last {periodDays} days
            </div>
            <div className="text-sm text-amber-600 dark:text-amber-400">
              Try selecting a longer period from the dropdown above to see historical impact.
            </div>
          </div>
        )}

        {!loading && !error && data && data.summary?.total_count > 0 && (
          <>
            {renderHero(data.summary, data.config)}
            {renderKpiCards(data.summary, data.config)}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {renderCostComparison(data.summary)}
              {renderTrend(data.trend)}
            </div>
            {renderTopEntities(data.top_entities, data.config)}
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default ImpactDashboard;
